import React from 'react';
import Icon from 'common/components/Icon';
import Portal from 'common/portal';
import useOnClickOutside from 'common/hooks/useOnClickOutside';
import {
  BottomContainer,
  ModalOverlay,
  ModalSubtitle,
  ModalContainer,
  ModalHeading,
  ModalContent,
  ModalHeader,
  CloseButtonContainer,
  ModalTitleIcon,
} from 'common/components/Modal/styled';

export interface ModalV2Props {
  isOpen: boolean;
  clickOutsideClose?: boolean;
  title?: string;
  subtitle?: string;
  modalId: string;
  customWidth?: number;
  children: React.ReactNode;
  bottomContent?: React.ReactNode;
  customSpacing?: number[];
  /** `card` is a plain white dialog with a left-aligned title (used by the settings pages); the default keeps the tinted frame. */
  appearance?: 'default' | 'card';
  /** Card only: a round icon before the title. */
  titleIcon?: { node: React.ReactNode; tone: 'red' | 'purple' };
  /** Card only: the horizontal inset of the header in px (default 24), for roomier modals such as the guides. */
  inset?: number;
  /** Card only: `page` styles the title like a settings page header (large, bold, black rule underneath). */
  headerStyle?: 'default' | 'page';
  onClose: () => void;
  'data-testid'?: string;
}

const ModalV2 = ({
  isOpen,
  clickOutsideClose = true,
  title,
  subtitle,
  modalId,
  children,
  bottomContent,
  customSpacing = [1],
  customWidth = 524,
  appearance = 'default',
  titleIcon,
  inset,
  headerStyle = 'default',
  onClose,
  'data-testid': dataTestid,
}: ModalV2Props): JSX.Element => {
  const [ready, setReady] = React.useState(false);
  const innerRef = React.useRef<HTMLDivElement | undefined>();

  React.useEffect(() => {
    if (isOpen) setReady(true);
  }, [isOpen]);

  useOnClickOutside(innerRef as React.RefObject<HTMLElement>, () => {
    isOpen && clickOutsideClose && onClose();
  });

  return (
    <Portal wrapperId={modalId}>
      {isOpen && (
        <ModalOverlay data-ready={ready} aria-hidden={!isOpen} data-appearance={appearance}>
          <ModalContainer
            ref={innerRef as React.RefObject<HTMLDivElement>}
            spacing={customSpacing}
            width={customWidth}
            data-testid={dataTestid}
            data-appearance={appearance}
            style={inset ? ({ '--modal-inset': `${inset}px` } as React.CSSProperties) : undefined}
          >
            <ModalHeader data-appearance={appearance} data-header={headerStyle}>
              {titleIcon && <ModalTitleIcon tone={titleIcon.tone}>{titleIcon.node}</ModalTitleIcon>}
              {title && (
                <ModalHeading data-testid='modal-title' data-appearance={appearance} data-header={headerStyle}>
                  {title}
                  {subtitle && <ModalSubtitle data-testid='modal-subtitle'>{subtitle}</ModalSubtitle>}
                </ModalHeading>
              )}
              {clickOutsideClose && (
                <CloseButtonContainer onClick={onClose} data-appearance={appearance}>
                  <Icon icon='x' />
                </CloseButtonContainer>
              )}
            </ModalHeader>
            <ModalContent>{children}</ModalContent>
            {bottomContent && <BottomContainer>{bottomContent}</BottomContainer>}
          </ModalContainer>
        </ModalOverlay>
      )}
    </Portal>
  );
};

export default ModalV2;
